// Tax Policy layer (phase 4) — Rules tab.
//
// A rule answers "whether and on what base" the geographically resolved
// rates apply, keyed on customer segment / business units / state / job type
// (an empty axis = any; most specific match wins). Effects: narrow the taxable
// bases (stored as labor / materials, shown as Service / Materials-Equipment),
// optionally force a tax group (the 0%-memberships pattern). Rules apply to
// documents priced from now on — existing snapshots are immutable.
//
// The editor is an INLINE panel, not a dialog: it sits beside a live calculator
// so the operator sees what the rule does to real money before saving it.

import { useCallback, useMemo, useRef, useState } from 'react';
import { ServiceAreaZipPicker } from '@/components/service-areas/ServiceAreaZipPicker';
import { useServiceAreas } from '@/hooks/service-areas/useServiceAreas';
import { useTaxZones } from '@/hooks/taxes/useTaxZones';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft, Calculator, Pencil, Plus, Trash2, X } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Badge } from '@/components/atoms/badge';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { Checkbox } from '@/components/atoms/checkbox';
import { Switch } from '@/components/atoms/switch';
import { Card } from '@/components/atoms/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/atoms/table';
import { EmptyState } from '@/components/atoms/EmptyState';
import { Fullbleed } from '@/contexts/fullbleed';
import { EntityIndexHeader } from '@/components/layouts';
import { TaxRulesHelp } from '@/components/organisms/taxes/TaxRulesHelp';
import {
  useCreateTaxPolicy,
  useDeleteTaxPolicy,
  useTaxPolicies,
  useUpdateTaxPolicy,
} from '@/hooks/taxes/useTaxPolicies';
import { useTaxGroups } from '@/hooks/taxes/useTaxGroups';
import {
  TaxSimulatorPanel,
  type DraftTaxRule,
} from '@/components/organisms/taxes/TaxSimulatorPanel';
import { useBusinessUnits } from '@/hooks/scheduler/useBusinessUnits';
import { useJobTypes } from '@/hooks/settings/useJobTypes';
import {
  explainPolicyMismatch,
  policyScoreParts,
  rankTaxPolicyPreview,
} from '@/lib/tax/tax-policy-preview';
import type { TaxPolicy, TaxPolicyBase, TaxPolicyInput } from '@/types/tax';

export const Route = createFileRoute('/_dashboard/dashboard/taxes/rules')({
  component: TaxRulesPage,
});

const ANY = '__any__';
/**
 * Segment is UNCHOSEN. Every rule is residential or commercial, and every unit
 * on it must share that segment, so the segment has to be decided before there
 * is a coherent list of units to choose from.
 */
const UNSET = '__unset__';

const SEGMENT_LABELS: Record<string, string> = {
  RESIDENTIAL: 'Residential',
  COMMERCIAL: 'Commercial',
};

/**
 * Which units a segment admits: a RESIDENTIAL unit is offered only under
 * Residential, a COMMERCIAL one only under Commercial. Mirrors the resolver,
 * where the BU named on the document decides the segment.
 */
function buFitsSegment(
  units: { id: string; segment?: string }[],
  unitId: string,
  segment: string,
): boolean {
  if (segment === UNSET) return false;
  const unit = units.find((u) => u.id === unitId);
  if (!unit?.segment) return true;
  return unit.segment === segment;
}

/**
 * What the operator calls each base. The STORED values are unchanged
 * ('labor' | 'materials' | 'freight') — these are display labels only, so no
 * rule is repriced by renaming them.
 */
const BASE_LABELS: Record<Exclude<TaxPolicyBase, 'all'>, string> = {
  labor: 'Service',
  materials: 'Materials/Equipment',
  freight: 'Freight',
};

/**
 * Freight is no longer OFFERED. It stays in BASE_LABELS because a rule saved
 * before this change may still store it, and a summary that silently omitted it
 * would misdescribe that rule. Existing values ride through the editor
 * untouched (see editorFromPolicy) — removing the checkbox must not quietly
 * change what a saved rule taxes.
 */
const NARROWABLE_BASES: Exclude<TaxPolicyBase, 'all'>[] = ['labor', 'materials'];

/** What one override can stand in for. */
const JURISDICTION_LAYERS = [
  { value: 'all', label: 'Everything' },
  { value: 'state', label: 'State only' },
  { value: 'county', label: 'County only' },
  { value: 'city', label: 'City only' },
  { value: 'district', label: 'District only' },
] as const;

/** One override: a percentage, and the layer it replaces. */
interface BaseRateRow {
  /** Percent as typed, so a half-typed "5." does not become NaN mid-keystroke. */
  percent: string;
  jurisdictionType: string;
  /** Empty = print the name of the tax this row replaces. */
  label: string;
  /** ALL | INCLUDE | EXCLUDE, within the rule's own area scope. */
  zipMode: string;
  /** Chosen from the ZIPs the rule itself covers. */
  zips: string[];
  /** Keep the row off customer documents (no name or rate; nothing at 0%). */
  hideOnDocument: boolean;
}

/** Where ONE override applies, within the rule's own area scope. */
const ZIP_MODES = [
  { value: 'ALL', label: 'Wherever the rule applies' },
  { value: 'INCLUDE', label: 'Only in these ZIPs' },
  { value: 'EXCLUDE', label: 'Except in these ZIPs' },
] as const;

/** Trims float noise from rate * 100 (0.051 * 100 = 5.1000000000000005). */
function round4(n: number): number {
  return Math.round(n * 10000) / 10000;
}

/**
 * The rate overrides a rule carries, in words: "Service at 8.25%". A rule that
 * SETS a rate is doing something louder than narrowing, and "All bases taxable"
 * on its own hid it — the list read the same for a rule that charges a flat
 * 8.25% on services as for one that changes nothing.
 */
function overridesSummary(policy: TaxPolicy): string[] {
  return (policy.baseRates ?? []).map((r) => {
    const base = BASE_LABELS[r.base as Exclude<TaxPolicyBase, 'all'>] ?? r.base;
    const layer =
      r.jurisdictionType && r.jurisdictionType !== 'all' ? ` (${r.jurisdictionType} layer)` : '';
    const zips = r.zips ?? [];
    const scope =
      zips.length > 0 && r.zipMode && r.zipMode !== 'ALL'
        ? `, ${r.zipMode === 'EXCLUDE' ? 'except' : 'only in'} ${zips.length} ZIP${zips.length === 1 ? '' : 's'}`
        : '';
    const hidden = r.hideOnDocument ? ', hidden on documents' : '';
    return `${base} at ${+(r.rate * 100).toFixed(4)}%${layer}${scope}${hidden}`;
  });
}

function basesSummary(policy: TaxPolicy): string {
  if (policy.taxableBases.includes('all')) return 'All bases taxable';
  if (policy.taxableBases.length === 0) return 'Nothing taxable';
  const labels = policy.taxableBases
    .filter((b): b is Exclude<TaxPolicyBase, 'all'> => b !== 'all')
    .map((b) => BASE_LABELS[b] ?? b);
  return `Only ${labels.join(' + ')} taxable`;
}

// ─── Editor dialog ───────────────────────────────────────────────────────────

interface EditorState {
  name: string;
  /** Many units, one rule. Empty means "any unit". */
  businessUnitIds: string[];
  segment: string;
  stateCode: string;
  jobTypeId: string;
  taxGroupId: string;
  /** Ticked bases. All of them ticked means "the jurisdiction decides" (['all']). */
  bases: Set<Exclude<TaxPolicyBase, 'all'>>;
  /** Overrides per base. Empty list = the jurisdiction decides that base. */
  baseRates: Partial<Record<Exclude<TaxPolicyBase, 'all'>, BaseRateRow[]>>;
  priority: string;
  exemptByDefault: boolean;
  /** Location axis — empty means the rule applies anywhere. */
  serviceAreaZips: string[];
  /** Every area, including ones added later — only meaningful with all selected. */
  allServiceAreas: boolean;
}

function editorFromPolicy(policy: TaxPolicy | null): EditorState {
  const narrows = policy ? !policy.taxableBases.includes('all') : false;
  return {
    name: policy?.name ?? '',
    businessUnitIds:
      policy?.businessUnitIds ?? (policy?.businessUnitId ? [policy.businessUnitId] : []),
    segment: policy?.segment ?? UNSET,
    stateCode: policy?.stateCode ?? ANY,
    jobTypeId: policy?.jobTypeId ?? ANY,
    taxGroupId: policy?.taxGroupId ?? ANY,
    bases: new Set(
      narrows
        ? (policy?.taxableBases.filter((b): b is Exclude<TaxPolicyBase, 'all'> => b !== 'all') ??
            [])
        : NARROWABLE_BASES,
    ),
    baseRates: (policy?.baseRates ?? []).reduce<
      Partial<Record<Exclude<TaxPolicyBase, 'all'>, BaseRateRow[]>>
    >((acc, r) => {
      const key = r.base as Exclude<TaxPolicyBase, 'all'>;
      (acc[key] ??= []).push({
        percent: String(round4(r.rate * 100)),
        jurisdictionType: r.jurisdictionType ?? 'all',
        label: r.label ?? '',
        zipMode: r.zipMode ?? 'ALL',
        zips: r.zips ?? [],
        hideOnDocument: r.hideOnDocument === true,
      });
      return acc;
    }, {}),
    priority: String(policy?.priority ?? 100),
    exemptByDefault: policy?.exemptByDefault ?? false,
    serviceAreaZips: (policy?.zips ?? []).map((z) => z.zipCode),
    allServiceAreas: policy?.allServiceAreas === true,
  };
}

function editorToInput(state: EditorState): TaxPolicyInput {
  return {
    name: state.name.trim(),
    // Always sent: [] is meaningful (clears the unit axis), exactly like zips.
    businessUnitIds: state.businessUnitIds,
    // Only reachable once a segment is chosen — saving is locked until then.
    segment: state.segment as TaxPolicyInput['segment'],
    stateCode: state.stateCode === ANY ? null : state.stateCode,
    jobTypeId: state.jobTypeId === ANY ? null : state.jobTypeId,
    taxGroupId: state.taxGroupId === ANY ? null : state.taxGroupId,
    // Every offered base ticked is exactly "no narrowing" — the ['all']
    // sentinel — so the operator never has to say it twice.
    taxableBases: NARROWABLE_BASES.every((b) => state.bases.has(b))
      ? ['all']
      : ([...state.bases] as TaxPolicyBase[]),
    // Whole-set replace. Only bases the rule actually taxes may carry an
    // override — one on an untaxed base would be a charge nobody can see.
    baseRates: NARROWABLE_BASES.filter((b) => state.bases.has(b)).flatMap((b) =>
      (state.baseRates[b] ?? []).map((row) => ({
        base: b,
        rate: (Number.parseFloat(row.percent) || 0) / 100,
        jurisdictionType: row.jurisdictionType,
        label: row.label.trim() || undefined,
        zipMode: row.zipMode,
        zips: row.zips,
        hideOnDocument: row.hideOnDocument,
      })),
    ),
    priority: Number.parseInt(state.priority, 10) || 100,
    exemptByDefault: state.exemptByDefault,
    // Always sent: [] is meaningful here (clears the location axis).
    serviceAreaZips: state.serviceAreaZips,
    allServiceAreas: state.allServiceAreas,
  };
}

/**
 * Multi-unit picker. A rule must name at least one unit: an empty selection used
 * to mean "any unit", which read on screen exactly like "no unit".
 */
function BusinessUnitMultiSelect({
  units,
  selected,
  onChange,
}: {
  units: { id: string; name: string; segment?: string }[];
  selected: string[];
  onChange: (ids: string[]) => void;
}) {
  const chosen = new Set(selected);
  if (units.length === 0) {
    return (
      <p className="rounded-md border border-dashed px-3 py-2 text-xs text-muted-foreground">
        No units match this segment.
      </p>
    );
  }
  return (
    <div className="grid gap-1.5">
      <div className="max-h-36 overflow-y-auto rounded-md border p-2">
        {units.map((bu) => (
          <div key={bu.id} className="flex items-center gap-1.5 py-0.5">
            <Checkbox
              id={`bu-${bu.id}`}
              checked={chosen.has(bu.id)}
              onCheckedChange={(v) =>
                onChange(v === true ? [...selected, bu.id] : selected.filter((x) => x !== bu.id))
              }
            />
            <Label htmlFor={`bu-${bu.id}`} className="font-normal">
              {bu.name}
            </Label>
            {bu.segment ? (
              <span className="text-xs text-muted-foreground">
                {SEGMENT_LABELS[bu.segment] ?? bu.segment}
              </span>
            ) : null}
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        {chosen.size === 0
          ? 'Any unit — the rule is not limited by unit.'
          : `${chosen.size} unit${chosen.size === 1 ? '' : 's'} selected.`}
      </p>
    </div>
  );
}

function TaxRuleEditor({
  state,
  setState,
  policy,
  onClose,
  calcOpen,
  onToggleCalc,
}: {
  state: EditorState;
  setState: React.Dispatch<React.SetStateAction<EditorState>>;
  policy: TaxPolicy | null;
  onClose: () => void;
  calcOpen: boolean;
  onToggleCalc: () => void;
}) {
  const { data: businessUnits } = useBusinessUnits('active');
  const create = useCreateTaxPolicy();
  const update = useUpdateTaxPolicy();

  const { data: serviceAreas = [] } = useServiceAreas();
  const { data: taxZones = [] } = useTaxZones();
  // A rule scopes TAX geography, which is wider than dispatch coverage:
  // manual tax zones exist precisely for ZIPs the tenant taxes but does
  // not dispatch to. Offer the union — coverage rows first, then tax-zone
  // ZIPs coverage doesn't know, synthesized into the picker's row shape.
  const pickableAreas = useMemo(() => {
    const covered = new Set(serviceAreas.map((a) => a.zipCode));
    const extras = taxZones
      .filter(
        (z): z is typeof z & { zipCode: string } => Boolean(z.zipCode) && !covered.has(z.zipCode!),
      )
      .map((z) => ({
        id: `tax-zone:${z.id}`,
        zipCode: z.zipCode,
        city: z.city ?? '',
        state: z.stateCode ?? '',
      }));
    return [...serviceAreas, ...(extras as typeof serviceAreas)];
  }, [serviceAreas, taxZones]);
  const pickableZips = useMemo(
    () => [...new Set(pickableAreas.map((a) => a.zipCode.trim().toUpperCase().replace(/\s/g, '')))],
    [pickableAreas],
  );
  // The unit list the segment admits. Filtering here (not in the picker) keeps
  // the picker dumb and the eligibility rule in one place.
  const eligibleUnits = useMemo(
    () =>
      (businessUnits ?? []).filter((bu) =>
        buFitsSegment(businessUnits ?? [], bu.id, state.segment),
      ),
    [businessUnits, state.segment],
  );

  // Nothing downstream of the segment means anything until it is chosen.
  const locked = state.segment === UNSET;
  const saving = create.isPending || update.isPending;
  // empty bases = "nothing taxable", which is a legitimate rule.
  // A rule must name the units it covers — the server refuses one that does not.
  // One override per (base, layer): the server refuses a second, because it
  // would replace the first on save rather than add to it.
  const duplicateOverride = NARROWABLE_BASES.filter((b) => state.bases.has(b)).find((b) => {
    const layers = (state.baseRates[b] ?? []).map((row) => row.jurisdictionType || 'all');
    return new Set(layers).size !== layers.length;
  });
  const valid =
    state.name.trim().length > 0 &&
    state.businessUnitIds.length > 0 &&
    !duplicateOverride &&
    !locked;

  const save = async () => {
    const input = editorToInput(state);
    if (policy) {
      await update.mutateAsync({ id: policy.id, ...input });
    } else {
      await create.mutateAsync(input);
    }
    onClose();
  };

  return (
    <Card className="flex flex-col">
      <div className="flex items-center justify-between border-b px-4 py-2.5">
        <h2 className="text-sm font-semibold">{policy ? 'Edit tax rule' : 'New tax rule'}</h2>
        <div className="flex items-center gap-1">
          <Button
            variant={calcOpen ? 'secondary' : 'outline'}
            size="sm"
            className="h-7 gap-1.5 px-2 text-xs"
            onClick={onToggleCalc}
          >
            <Calculator size={12} />
            Calculator
          </Button>
          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={onClose}>
            <X size={12} />
          </Button>
        </div>
      </div>
      {/* No inner scroller: the form renders at its full height and the PAGE
          scrolls. A card that scrolls inside a page that doesn't hides its own
          footer and puts two scrollbars a few pixels apart. */}
      <div className="p-4">
        <div className="grid gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor="rule-name">Name</Label>
            <Input
              id="rule-name"
              placeholder="Residential repair — Chicago Loop"
              value={state.name}
              onChange={(e) => setState((s) => ({ ...s, name: e.target.value }))}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            {/* Segment leads: it decides which units are even eligible below. */}
            <div className="grid gap-1.5">
              <Label>Customer segment</Label>
              <Select
                value={state.segment}
                onValueChange={(v) =>
                  setState((s) => ({
                    ...s,
                    segment: v,
                    // Units that no longer belong to the chosen segment must not
                    // survive the switch — a rule keeping an invisible unit is a
                    // rule nobody can read off the screen.
                    businessUnitIds: s.businessUnitIds.filter((id) =>
                      buFitsSegment(businessUnits ?? [], id, v),
                    ),
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a segment…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="RESIDENTIAL">Residential</SelectItem>
                  <SelectItem value="COMMERCIAL">Commercial</SelectItem>
                </SelectContent>
              </Select>
              {locked ? (
                <p className="text-xs text-muted-foreground">
                  Pick this first — it decides which units the rule may cover.
                </p>
              ) : null}
            </div>
            <fieldset disabled={locked} className={`contents ${locked ? 'opacity-50' : ''}`}>
              <div className="grid gap-1.5">
                <Label>Business units</Label>
                <BusinessUnitMultiSelect
                  units={eligibleUnits}
                  selected={state.businessUnitIds}
                  onChange={(ids) => setState((s) => ({ ...s, businessUnitIds: ids }))}
                />
                {state.businessUnitIds.length === 0 ? (
                  <p className="text-xs text-amber-700 dark:text-amber-500">
                    Pick at least one business unit. A rule covers only the units it names.
                  </p>
                ) : null}
              </div>
              {/* Rules are scoped by the areas and ZIPs they name, not by state. A
                  rule saved with a state keeps it until removed here — shown, never
                  silently carried or silently dropped. */}
              {state.stateCode !== ANY ? (
                <div className="flex flex-wrap items-center gap-2 rounded-md border border-amber-600/30 bg-amber-600/10 px-3 py-2 text-xs sm:col-span-2">
                  <span className="min-w-0 flex-1">
                    This rule is also limited to the state <strong>{state.stateCode}</strong>.
                    States are no longer offered: pick the areas and ZIPs it covers instead.
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 px-2 text-xs"
                    onClick={() => setState((s) => ({ ...s, stateCode: ANY }))}
                  >
                    Remove state
                  </Button>
                </div>
              ) : null}
              <div className="grid gap-1.5 sm:col-span-2">
                <Label>Service areas</Label>
                <ServiceAreaZipPicker
                  subject="this rule"
                  serviceAreas={pickableAreas}
                  selectedZips={state.serviceAreaZips}
                  onChange={(zips) =>
                    setState((s) => ({
                      ...s,
                      serviceAreaZips: zips,
                      // "Also new areas" only means something while every area
                      // is picked; unticking one drops it.
                      allServiceAreas:
                        s.allServiceAreas && pickableZips.every((zip) => zips.includes(zip)),
                    }))
                  }
                  allowSelectAll
                  whenAllSelected={
                    <label className="flex items-start gap-2 pl-6 text-xs font-normal">
                      <Checkbox
                        className="mt-0.5"
                        checked={state.allServiceAreas}
                        onCheckedChange={(v) =>
                          setState((s) => ({ ...s, allServiceAreas: v === true }))
                        }
                        aria-label="Also apply to newly added areas"
                      />
                      <span>
                        Also apply to newly added areas
                        <span className="block text-muted-foreground">
                          An area added later in Settings → Service Areas joins this rule
                          automatically.
                        </span>
                      </span>
                    </label>
                  }
                />
                <p className="text-xs text-muted-foreground">
                  Pick the areas, or expand one for individual ZIPs, where this rule applies. Leave
                  empty and it applies wherever its business units work.
                </p>
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="rule-priority">Priority (tie-break, lower wins)</Label>
                <Input
                  id="rule-priority"
                  type="number"
                  min={0}
                  value={state.priority}
                  onChange={(e) => setState((s) => ({ ...s, priority: e.target.value }))}
                />
              </div>
            </fieldset>
          </div>

          <fieldset disabled={locked} className={`grid gap-4 ${locked ? 'opacity-50' : ''}`}>
            {/* ONE table, because there is one question per base: is it taxed,
                and if the rule sets the rate, at what. The old master
                "Limit which bases are taxable" checkbox is gone — unticking a
                base IS the limit, and a switch that only revealed two
                checkboxes was a step with nothing in it. */}
            <div className="grid gap-3 rounded-md border p-3">
              <div className="flex items-baseline justify-between">
                <p className="text-sm font-medium">Taxable bases</p>
                <p className="text-xs text-muted-foreground">
                  All ticked = the jurisdiction decides.
                </p>
              </div>

              {NARROWABLE_BASES.map((base) => {
                const rows = state.baseRates[base] ?? [];
                const taxed = state.bases.has(base);
                const ruleZips = state.serviceAreaZips;
                const setRows = (next: BaseRateRow[]) =>
                  setState((st) => ({
                    ...st,
                    baseRates: { ...st.baseRates, [base]: next },
                  }));
                return (
                  <div key={base} className="grid gap-2 rounded-md border p-2.5">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        id={`base-${base}`}
                        checked={taxed}
                        onCheckedChange={(v) =>
                          setState((st) => {
                            const bases = new Set(st.bases);
                            if (v === true) bases.add(base);
                            else bases.delete(base);
                            return { ...st, bases };
                          })
                        }
                      />
                      <Label htmlFor={`base-${base}`} className="font-normal">
                        {BASE_LABELS[base]}
                      </Label>
                      <span className="text-xs text-muted-foreground">
                        {!taxed
                          ? 'not taxed'
                          : rows.length === 0
                            ? 'the jurisdiction decides'
                            : `${rows.length} override${rows.length === 1 ? '' : 's'}`}
                      </span>
                    </div>

                    {/* Each row is one override the operator added on purpose, so
                        two rows on the same base both charge — 5.1% state plus 2%
                        city is 7.1%, which is what asking for both means. */}
                    {taxed ? (
                      <div className="grid gap-1.5 pl-7">
                        {rows.map((row, i) => (
                          <div key={i} className="grid gap-1.5 rounded-md border p-2">
                            <div className="flex flex-wrap items-center gap-2">
                              <Input
                                className="h-8 w-20"
                                inputMode="decimal"
                                aria-label={`${BASE_LABELS[base]} override ${i + 1} percent`}
                                value={row.percent}
                                onChange={(e) =>
                                  setRows(
                                    rows.map((r, j) =>
                                      j === i ? { ...r, percent: e.target.value } : r,
                                    ),
                                  )
                                }
                              />
                              <span className="text-xs text-muted-foreground">%</span>
                              <Select
                                value={row.jurisdictionType}
                                onValueChange={(v) =>
                                  setRows(
                                    rows.map((r, j) =>
                                      j === i ? { ...r, jurisdictionType: v } : r,
                                    ),
                                  )
                                }
                              >
                                <SelectTrigger className="h-8 w-40 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {JURISDICTION_LAYERS.map((l) => (
                                    <SelectItem key={l.value} value={l.value}>
                                      {l.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Input
                                className="h-8 min-w-0 flex-1 text-xs"
                                // Blank is a real choice, not an empty field: it
                                // prints the name of the tax this row replaces.
                                placeholder="Use the original tax name"
                                aria-label={`${BASE_LABELS[base]} override ${i + 1} name`}
                                value={row.label}
                                onChange={(e) =>
                                  setRows(
                                    rows.map((r, j) =>
                                      j === i ? { ...r, label: e.target.value } : r,
                                    ),
                                  )
                                }
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon-sm"
                                title="Remove this override"
                                onClick={() => setRows(rows.filter((_, j) => j !== i))}
                              >
                                <X size={13} />
                              </Button>
                            </div>

                            {/* Where THIS override applies, drawn from the ZIPs the
                                rule already covers — an override cannot reach an
                                address the rule itself never matches. */}
                            <div className="flex flex-wrap items-center gap-2 pl-1">
                              <Select
                                value={row.zipMode}
                                onValueChange={(v) =>
                                  setRows(rows.map((r, j) => (j === i ? { ...r, zipMode: v } : r)))
                                }
                              >
                                <SelectTrigger className="h-7 w-52 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {ZIP_MODES.map((m) => (
                                    <SelectItem key={m.value} value={m.value}>
                                      {m.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {row.zipMode === 'ALL' ? null : ruleZips.length === 0 ? (
                                <span className="text-xs text-muted-foreground">
                                  Pick service areas above first.
                                </span>
                              ) : (
                                <div className="flex flex-wrap items-center gap-2">
                                  {ruleZips.map((z) => (
                                    <label
                                      key={z}
                                      className="flex items-center gap-1 text-xs font-normal"
                                    >
                                      <Checkbox
                                        checked={row.zips.includes(z)}
                                        onCheckedChange={(v) =>
                                          setRows(
                                            rows.map((r, j) =>
                                              j === i
                                                ? {
                                                    ...r,
                                                    zips:
                                                      v === true
                                                        ? [...r.zips, z]
                                                        : r.zips.filter((x) => x !== z),
                                                  }
                                                : r,
                                            ),
                                          )
                                        }
                                      />
                                      {z}
                                    </label>
                                  ))}
                                </div>
                              )}
                            </div>

                            {/* What the customer sees of this row. The money is
                                never hidden — only the line describing it. */}
                            <label className="flex items-start gap-2 pl-1 text-xs font-normal">
                              <Checkbox
                                className="mt-0.5"
                                checked={row.hideOnDocument}
                                aria-label={`${BASE_LABELS[base]} override ${i + 1} hide on customer documents`}
                                onCheckedChange={(v) =>
                                  setRows(
                                    rows.map((r, j) =>
                                      j === i ? { ...r, hideOnDocument: v === true } : r,
                                    ),
                                  )
                                }
                              />
                              <span>
                                Hide on customer documents
                                <span className="block text-muted-foreground">
                                  No tax name or rate is printed for it, and at 0% it does not
                                  appear at all. Anything it charges still shows in the total, as
                                  plain “Tax”.
                                </span>
                              </span>
                            </label>
                          </div>
                        ))}
                        <div>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-7 gap-1.5 px-2 text-xs"
                            onClick={() =>
                              // 0% by default: an override that changes nothing
                              // until a number is typed cannot surprise anyone.
                              setRows([
                                ...rows,
                                {
                                  percent: '0',
                                  jurisdictionType: 'all',
                                  label: '',
                                  zipMode: 'ALL',
                                  zips: [],
                                  hideOnDocument: false,
                                },
                              ])
                            }
                          >
                            <Plus size={12} />
                            Add override
                          </Button>
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })}

              {state.bases.size === 0 ? (
                <p className="text-xs text-amber-600">
                  No base ticked: matching documents pay no tax at all.
                </p>
              ) : null}

              <p className="border-t pt-3 text-xs text-muted-foreground">
                An override REPLACES the jurisdiction for the layer it names — the number you type
                is the number the customer pays for that layer. Two overrides on one base both
                apply. Leave the name blank and the customer sees the name of the tax it replaced;
                type one and they see that instead. This is the only setting that can raise tax
                above what the address imposes.
              </p>
            </div>
          </fieldset>

          {duplicateOverride ? (
            <p className="text-xs text-amber-700 dark:text-amber-500">
              {BASE_LABELS[duplicateOverride]} has two overrides on the same layer. A base can have
              one override per layer — change one of them or remove it.
            </p>
          ) : null}
          <p className="text-xs text-muted-foreground">
            Applies to documents priced from now on — already-priced estimates, jobs and invoices
            keep their frozen tax.
          </p>
        </div>
      </div>
      <div className="flex justify-end gap-2 border-t px-4 py-2.5">
        <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button size="sm" onClick={() => void save()} disabled={!valid || saving}>
          {policy ? 'Save rule' : 'Create rule'}
        </Button>
      </div>
    </Card>
  );
}

// ─── Split layout ───────────────────────────────────────────────────────────

/**
 * Two panels sharing the PAGE's scroll, with a drag handle between them.
 *
 * Not a scroller-per-panel: the rule and its calculator are read together, so
 * they have to move together. The handle only changes how the width is divided;
 * below `lg` the pair stacks and the handle is withdrawn, since a drag target
 * that thin is unusable on a narrow screen anyway.
 */
function SplitPanes({ left, right }: { left: React.ReactNode; right: React.ReactNode }) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [leftPct, setLeftPct] = useState(58);
  const [dragging, setDragging] = useState(false);

  const startDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(true);
    const move = (ev: PointerEvent) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect || rect.width === 0) return;
      const pct = ((ev.clientX - rect.left) / rect.width) * 100;
      // Bounded so neither panel can be dragged away entirely.
      setLeftPct(Math.min(75, Math.max(30, pct)));
    };
    const stop = () => {
      setDragging(false);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop);
  };

  return (
    <div ref={containerRef} className="flex flex-col gap-3 lg:flex-row lg:gap-0">
      <div className="min-w-0 lg:pr-1.5" style={{ flexBasis: `${leftPct}%` }}>
        {left}
      </div>
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize panels"
        onPointerDown={startDrag}
        className={`hidden w-3 shrink-0 cursor-col-resize items-center justify-center lg:flex ${
          dragging ? 'opacity-100' : 'opacity-60 hover:opacity-100'
        }`}
      >
        <div className="h-16 w-1 rounded-full bg-border" />
      </div>
      <div className="min-w-0 flex-1 lg:pl-1.5">{right}</div>
    </div>
  );
}

// ─── Live calculator ────────────────────────────────────────────────────────

/**
 * The calculator is the REAL simulator — the same component the Tax Simulator
 * page runs, not a second implementation that could drift from it. The only
 * extra is draftPolicy: the rule being typed is not in the database, so it is
 * sent with each simulation and the server prices it as if saved, in place of
 * its saved version.
 */
function TaxRuleCalculator({
  draft,
  editingId,
  draftDirty,
  onOpenRule,
  onClose,
}: {
  draft: EditorState;
  editingId: string | null;
  /** The draft differs from what is saved — opening another rule would drop it. */
  draftDirty: boolean;
  /** Open a saved rule in the editor (e.g. the one that beats this draft). */
  onOpenRule: (policy: TaxPolicy) => void;
  onClose: () => void;
}) {
  // "Apply it anyway": show this rule's effect on a document it does not claim.
  // It is a question about ONE rule and ONE document, not a mode to carry around.
  // Remembered PER RULE and derived, so opening another rule starts un-forced
  // without an effect having to reset anything.
  const [forced, setForced] = useState<{ ruleId: string | null; on: boolean }>({
    ruleId: null,
    on: false,
  });
  const forceRule = forced.on && forced.ruleId === editingId;
  const setForceRule = (on: boolean) => setForced({ ruleId: editingId, on });
  // Opening another rule replaces the draft, so unsaved edits ask first.
  const [pendingOpen, setPendingOpen] = useState<TaxPolicy | null>(null);
  const openRule = (policy: TaxPolicy) => {
    if (draftDirty) setPendingOpen(policy);
    else onOpenRule(policy);
  };
  const { data: allPolicies } = useTaxPolicies({ includeInactive: false });
  const [ctx, setCtx] = useState<{
    zipCode: string;
    stateCode: string | null;
    businessUnitId: string | null;
    segment: string | null;
    jobTypeId: string | null;
  } | null>(null);
  const onContextChange = useCallback(
    (next: {
      zipCode: string;
      stateCode: string | null;
      businessUnitId: string | null;
      segment: string | null;
      jobTypeId: string | null;
    }) => setCtx(next),
    [],
  );

  /**
   * The draft standing in as a saved rule, so it competes in the same ranking
   * as everything already stored. Without it the banner could only ever report
   * on rules that exist, which is the opposite of what this editor is for.
   */
  const match = useMemo(() => {
    // Until a segment is chosen the draft is not a rule yet — nothing to rank.
    if (!ctx || draft.segment === UNSET) return null;
    const draftAsPolicy: TaxPolicy = {
      id: editingId ?? '__draft__',
      name: draft.name.trim() || 'This rule',
      isActive: true,
      priority: Number.parseInt(draft.priority, 10) || 100,
      // The set is authoritative; the scalar stays null so nothing reads it.
      businessUnitId: null,
      businessUnitIds: draft.businessUnitIds,
      segment: draft.segment as TaxPolicy['segment'],
      stateCode: draft.stateCode === ANY ? null : draft.stateCode,
      jobTypeId: draft.jobTypeId === ANY ? null : draft.jobTypeId,
      taxableBases: NARROWABLE_BASES.every((b) => draft.bases.has(b))
        ? ['all']
        : ([...draft.bases] as TaxPolicy['taxableBases']),
      taxGroupId: draft.taxGroupId === ANY ? null : draft.taxGroupId,
      exemptByDefault: draft.exemptByDefault,
      zips: draft.serviceAreaZips.map((zipCode) => ({ zipCode })),
      // Effects, not axes — they never affect whether the rule matches.
      baseRates: [],
      createdAt: '',
      updatedAt: '',
    };
    // Editing an existing rule: the draft REPLACES its saved self, or the same
    // rule would appear twice and compete with a stale copy of itself.
    const others = (allPolicies ?? []).filter((r) => r.id !== editingId);
    const matchCtx = {
      businessUnitId: ctx.businessUnitId,
      // Server parity: an unknown customer is priced as residential.
      segment: ctx.segment ?? 'RESIDENTIAL',
      stateCode: ctx.stateCode,
      zipCode: ctx.zipCode,
      // Only a loaded document has one; a rule scoped to a job type otherwise
      // (correctly) reports that nothing here names a job type.
      jobTypeId: ctx.jobTypeId,
    };
    return {
      ranked: rankTaxPolicyPreview([...others, draftAsPolicy], matchCtx),
      miss: explainPolicyMismatch(draftAsPolicy, matchCtx),
      matchCtx,
      draftAsPolicy,
    };
  }, [ctx, allPolicies, draft, editingId]);

  const draftId = editingId ?? '__draft__';
  const ranked = match?.ranked ?? null;
  const miss = match?.miss ?? null;
  const winner = ranked?.[0] ?? null;
  const draftMatches = Boolean(ranked?.some((r) => r.policy.id === draftId));
  const draftWins = winner?.policy.id === draftId;
  const losers = (ranked ?? []).filter((r) => r.policy.id !== draftId);
  const draftRank = ranked?.find((r) => r.policy.id === draftId) ?? null;
  const scoreText = (policy: TaxPolicy | undefined) => {
    if (!policy || !match) return '';
    const parts = policyScoreParts(policy, match.matchCtx);
    return parts.length === 0
      ? 'no axis (0)'
      : `${parts.map((p) => `${p.label} ${p.weight}`).join(' + ')} = ${parts.reduce((sum, p) => sum + p.weight, 0)}`;
  };

  const draftPolicy = useMemo<DraftTaxRule>(() => {
    const input = editorToInput(draft);
    return {
      ...input,
      // The save request requires a name; a rule still being typed may not have
      // one yet, and the preview must not fail for it.
      name: input.name || 'This rule',
      ...(editingId ? { replacesId: editingId } : {}),
      // Forcing a rule that already wins changes nothing, so it is not sent.
      ...(forceRule && !draftWins ? { force: true } : {}),
    };
  }, [draft, editingId, forceRule, draftWins]);

  return (
    <Card className="flex flex-col">
      <div className="flex shrink-0 items-start justify-between border-b px-4 py-2.5">
        <div>
          <h2 className="text-sm font-semibold">Live calculator</h2>
          <p className="text-xs text-muted-foreground">
            What this rule does to a real document, as you edit it.
          </p>
        </div>
        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={onClose}>
          <X size={12} />
        </Button>
      </div>
      <div className="border-b p-3">
        {!ctx?.zipCode ? (
          <div className="rounded-md border border-dashed px-3 py-2 text-xs text-muted-foreground">
            Pick a service location below to see whether this rule claims the document.
          </div>
        ) : draftWins ? (
          <div className="rounded-md border border-emerald-600/30 bg-emerald-600/10 px-3 py-2 text-xs">
            <span className="font-medium text-emerald-700 dark:text-emerald-400">
              {draft.name.trim() || 'This rule'} applies.
            </span>{' '}
            It is the most specific rule matching this document.
            {draft.businessUnitIds.length === 0
              ? ' Pick a business unit to save it — until then it is previewed as covering every unit.'
              : null}
          </div>
        ) : draftMatches ? (
          <div className="rounded-md border border-amber-600/30 bg-amber-600/10 px-3 py-2 text-xs">
            {winner ? (
              <button
                type="button"
                className="font-medium text-amber-700 underline underline-offset-2 hover:text-amber-800 dark:text-amber-500"
                onClick={() => openRule(winner.policy)}
                title="Open this rule"
              >
                {winner.policy.name}
              </button>
            ) : null}{' '}
            <span className="font-medium text-amber-700 dark:text-amber-500">wins instead.</span>{' '}
            {winner && winner.score > (draftRank?.score ?? 0) ? (
              <>
                It is more specific: it names {scoreText(winner.policy)}; this rule names{' '}
                {scoreText(match?.draftAsPolicy)}. Specificity is compared before priority.
              </>
            ) : (
              <>
                Both are equally specific ({winner?.score}), so priority decides:{' '}
                {winner?.policy.priority} beats {draft.priority} (lower wins).
              </>
            )}
          </div>
        ) : (
          <div className="rounded-md border border-rose-600/30 bg-rose-600/10 px-3 py-2 text-xs">
            <span className="font-medium text-rose-700 dark:text-rose-400">
              {draft.name.trim() || 'This rule'} does not match.
            </span>{' '}
            {miss
              ? `${miss.axis} excludes it — the rule wants ${miss.wants}, this document has ${miss.has}.`
              : 'The jurisdiction applies as-is.'}
          </div>
        )}
        {losers.length > 0 ? (
          <div className="mt-2 grid gap-1">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Also matching
            </p>
            {losers.map((r) => (
              <button
                key={r.policy.id}
                type="button"
                onClick={() => openRule(r.policy)}
                className="-mx-1 flex items-center justify-between gap-2 rounded px-1 py-0.5 text-left text-xs hover:bg-muted"
                title="Open this rule"
              >
                <span className="truncate underline-offset-2 hover:underline">{r.policy.name}</span>
                <span className="shrink-0 text-muted-foreground">
                  priority {r.policy.priority} · specificity {r.score}
                </span>
              </button>
            ))}
          </div>
        ) : null}
        {ctx?.zipCode && !draftWins ? (
          <div
            className={`mt-2 rounded-md border px-3 py-2 text-xs ${
              forceRule ? 'border-sky-600/30 bg-sky-600/10' : ''
            }`}
          >
            <label className="flex items-center gap-2 font-medium">
              <Switch
                checked={forceRule}
                onCheckedChange={setForceRule}
                aria-label="Apply this rule anyway"
              />
              Apply it anyway
            </label>
            <p className="mt-1 text-muted-foreground">
              {forceRule
                ? `Showing what ${draft.name.trim() || 'this rule'} WOULD charge here, skipping who it applies to and which rule wins. Only this preview: a real document is still priced by the rule that matches it.`
                : 'See what this rule would do to this document even though it does not apply to it.'}
            </p>
          </div>
        ) : null}
        {pendingOpen ? (
          <div className="mt-2 flex flex-wrap items-center gap-2 rounded-md border px-3 py-2 text-xs">
            <span className="min-w-0 flex-1">
              Open “{pendingOpen.name}”? Your unsaved changes to this rule will be lost.
            </span>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs"
              onClick={() => setPendingOpen(null)}
            >
              Keep editing
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-7 px-2 text-xs"
              onClick={() => {
                const next = pendingOpen;
                setPendingOpen(null);
                onOpenRule(next);
              }}
            >
              Discard and open
            </Button>
          </div>
        ) : null}
      </div>
      <TaxSimulatorPanel
        draftPolicy={draftPolicy}
        compact
        onContextChange={onContextChange}
        className="min-h-0 flex-1"
      />
    </Card>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

function TaxRulesPage() {
  const { data: policies, isLoading } = useTaxPolicies({ includeInactive: true });
  const { data: businessUnits } = useBusinessUnits('all');
  const { data: taxGroups } = useTaxGroups();
  const { data: pageJobTypes } = useJobTypes();
  const remove = useDeleteTaxPolicy();
  const update = useUpdateTaxPolicy();
  const [editing, setEditing] = useState<TaxPolicy | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [calcOpen, setCalcOpen] = useState(true);
  // The draft lives here, not in the editor, because the calculator beside it
  // prices the rule as it is being typed — before anything is saved.
  const [draft, setDraft] = useState<EditorState>(() => editorFromPolicy(null));

  // Compared through the save payload, so only a change that would be SAVED counts.
  const draftDirty =
    JSON.stringify(editorToInput(draft)) !==
    JSON.stringify(editorToInput(editorFromPolicy(editing)));
  const openEditor = (policy: TaxPolicy | null) => {
    setEditing(policy);
    setDraft(editorFromPolicy(policy));
    setEditorOpen(true);
  };

  const buName = useMemo(() => {
    const map = new Map<string, string>();
    for (const bu of businessUnits ?? []) map.set(bu.id, bu.name);
    return map;
  }, [businessUnits]);
  const jobTypeName = useMemo(() => {
    const map = new Map<string, string>();
    for (const jt of pageJobTypes ?? []) map.set(jt.id, jt.name);
    return map;
  }, [pageJobTypes]);
  const groupName = useMemo(() => {
    const map = new Map<string, string>();
    for (const g of taxGroups ?? []) map.set(g.id, g.name);
    return map;
  }, [taxGroups]);

  const rows = policies ?? [];

  return (
    <>
      <Fullbleed />
      <div className="flex h-full flex-col gap-3 overflow-y-auto p-4">
        <EntityIndexHeader
          title="Tax rules"
          actions={
            <>
              <Button asChild size="sm" variant="outline" className="h-7 px-2 text-xs gap-1.5">
                <Link to="/dashboard/taxes">
                  <ArrowLeft size={12} />
                  Back to taxes
                </Link>
              </Button>
              <Button
                size="sm"
                className="h-7 px-2 text-xs gap-1.5"
                onClick={() => openEditor(null)}
              >
                <Plus size={12} />
                New rule
              </Button>
            </>
          }
        />

        {/* Editing takes the WHOLE window. The list and the primer are not
            reference material while a rule is being written — they only steal
            the height the form needs, which is what forced the inner scrollbar. */}
        {editorOpen ? (
          (() => {
            const editor = (
              <TaxRuleEditor
                state={draft}
                setState={setDraft}
                policy={editing}
                onClose={() => setEditorOpen(false)}
                calcOpen={calcOpen}
                onToggleCalc={() => setCalcOpen((v) => !v)}
              />
            );
            return calcOpen ? (
              <SplitPanes
                left={editor}
                right={
                  <TaxRuleCalculator
                    draft={draft}
                    editingId={editing?.id ?? null}
                    draftDirty={draftDirty}
                    onOpenRule={openEditor}
                    onClose={() => setCalcOpen(false)}
                  />
                }
              />
            ) : (
              editor
            );
          })()
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              Who pays tax on what — by business unit, customer segment and area. Rates come from
              the service address unless a rule sets one: a rule can narrow what is taxable, and can
              replace the rate for services or materials.
            </p>

            <TaxRulesHelp />
          </>
        )}

        {editorOpen ? null : rows.length === 0 && !isLoading ? (
          <EmptyState
            title="No tax rules yet"
            description="Without rules, every customer and unit is taxed exactly as the address's jurisdiction dictates. Add a rule when a segment or unit needs different treatment — e.g. residential repair labor exempt in Texas."
          />
        ) : (
          <div className="overflow-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Rule</TableHead>
                  <TableHead>Applies to</TableHead>
                  <TableHead>Effect</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-24" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((policy) => (
                  <TableRow key={policy.id}>
                    <TableCell className="font-medium">{policy.name}</TableCell>
                    {/* TableCell is nowrap by default: the badges must be allowed to
                        wrap INSIDE the cell, or a rule naming seven units stretches
                        the whole table into a horizontal scroll. */}
                    <TableCell className="max-w-[28rem] whitespace-normal">
                      <div className="flex flex-wrap gap-1">
                        {(() => {
                          // The set, not the legacy scalar — a multi-unit rule
                          // read as "Any unit" here. One badge per unit, so they wrap.
                          const ids = policy.businessUnitIds?.length
                            ? policy.businessUnitIds
                            : policy.businessUnitId
                              ? [policy.businessUnitId]
                              : [];
                          if (ids.length === 0) {
                            return (
                              <Badge variant="secondary" className="text-[11px]">
                                Any unit
                              </Badge>
                            );
                          }
                          // Every unit, one badge each: the row grows taller rather
                          // than hiding which units a rule covers behind a count.
                          return ids.map((id) => {
                            const name = buName.get(id) ?? 'Unit';
                            return (
                              <Badge
                                key={id}
                                variant="secondary"
                                className="max-w-full text-[11px]"
                                title={name}
                              >
                                <span className="truncate">{name}</span>
                              </Badge>
                            );
                          });
                        })()}
                        <Badge variant="secondary" className="text-[11px]">
                          {SEGMENT_LABELS[policy.segment] ?? policy.segment}
                        </Badge>
                        {policy.stateCode ? (
                          <Badge variant="secondary" className="text-[11px]">
                            {policy.stateCode}
                          </Badge>
                        ) : null}
                        <Badge variant="secondary" className="text-[11px]">
                          {policy.jobTypeId
                            ? (jobTypeName.get(policy.jobTypeId) ?? 'Job type')
                            : 'Any job type'}
                        </Badge>
                        <Badge
                          variant="secondary"
                          className="text-[11px]"
                          title={
                            policy.zips.length > 0
                              ? policy.zips.map((z) => z.zipCode).join(', ')
                              : undefined
                          }
                        >
                          {policy.allServiceAreas
                            ? 'All areas, incl. new'
                            : policy.zips.length === 0
                              ? 'Anywhere'
                              : policy.zips.length <= 3
                                ? policy.zips.map((z) => z.zipCode).join(', ')
                                : `${policy.zips.length} ZIPs`}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">{basesSummary(policy)}</div>
                      {overridesSummary(policy).map((line) => (
                        <div key={line} className="text-xs text-muted-foreground">
                          {line}
                        </div>
                      ))}
                      {policy.taxGroupId ? (
                        <div className="text-xs text-muted-foreground">
                          Forces group: {groupName.get(policy.taxGroupId) ?? policy.taxGroupId}
                        </div>
                      ) : null}
                      {policy.exemptByDefault ? (
                        <div className="text-xs text-muted-foreground">
                          New customers start tax-exempt
                        </div>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <button
                        type="button"
                        className="inline-flex"
                        title={policy.isActive ? 'Click to deactivate' : 'Click to activate'}
                        onClick={() =>
                          void update.mutateAsync({
                            id: policy.id,
                            name: policy.name,
                            isActive: !policy.isActive,
                          })
                        }
                      >
                        <Badge
                          variant={policy.isActive ? 'default' : 'outline'}
                          className="text-[11px]"
                        >
                          {policy.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </button>
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <Button
                          size="icon-sm"
                          variant="ghost"
                          title="Edit rule"
                          onClick={() => openEditor(policy)}
                        >
                          <Pencil size={13} />
                        </Button>
                        <Button
                          size="icon-sm"
                          variant="ghost"
                          title="Delete rule"
                          onClick={() =>
                            void remove.mutateAsync({ id: policy.id, name: policy.name })
                          }
                        >
                          <Trash2 size={13} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </>
  );
}
